<template>
  <section v-loading="loading" class="section-block">
    <SectionTitle title="會員中心 QRcode 設定" @edit="showEditModal = true" />

    <BaseElForm label-position="left" label-width="250px">
      <BaseElFormItem label="是否啟用">
        {{ displayData.enable }}
      </BaseElFormItem>
      <BaseElFormItem label="會員卡來源">
        {{ displayData.cardSource }}
      </BaseElFormItem>

      <!-- 內部系統會員卡：顯示會員條碼內容 -->
      <BaseElFormItem v-if="!configData.isExternal" label="會員條碼內容">
        {{ displayData.qrcodeContent }}
      </BaseElFormItem>

      <!-- 外部系統會員卡：顯示外部系統廠商與條碼類型 -->
      <template v-if="configData.isExternal">
        <BaseElFormItem label="外部系統廠商">
          {{ displayData.externalType }}
        </BaseElFormItem>
        <BaseElFormItem label="會員條碼類型">
          QR Code
        </BaseElFormItem>
      </template>
    </BaseElForm>

    <MemberCenterQRCodeSettingEditModal
      v-if="showEditModal"
      :configData="configData"
      :qrcodeSourceOptions="qrcodeSourceOptions"
      @close="showEditModal = false"
      @refresh="refresh"
    />
  </section>
</template>

<script>
import { defineComponent, reactive, ref, computed, set, onMounted } from 'vue'
import SectionTitle from '@/components/Title/SectionTitle.vue'
import MemberCenterQRCodeSettingEditModal from './MemberCenterQRCodeSettingEditModal.vue'
import { GetMemberCenterQRCode, GetMemberCenterQRCodeSourceOptions } from '@/api/member/memberCenter'
import { useShop } from '@/use/shop'
import { defaultQRCodeContentOptions, memberCardSourceOptions } from '@/config/memberCenter'

export default defineComponent({
  name: 'MemberCenterQRCodeSetting',
  components: {
    SectionTitle,
    MemberCenterQRCodeSettingEditModal,
  },
  emits: ['refresh'],
  setup (props, { emit }) {
    const { shopId } = useShop()
    const loading = ref(false)
    const showEditModal = ref(false)
    const configData = reactive({
      enable: false,
      type: 'qrcode',
      isExternal: false,
      externalType: '',
      defaultScope: '',
      defaultKey: '',
    })
    const qrcodeSourceOptions = ref([])

    // 計算顯示資料
    const displayData = computed(() => {
      // 啟用狀態
      const enable = configData.enable ? '啟用' : '關閉'

      // 會員卡來源
      const sourceOption = memberCardSourceOptions.find(
        opt => opt.isExternal === configData.isExternal,
      )
      const cardSource = sourceOption ? sourceOption.label : '-'

      // 會員條碼內容（內部系統）
      const contentOption = defaultQRCodeContentOptions.find(
        opt => opt.scope === configData.defaultScope && opt.key === configData.defaultKey,
      )
      const qrcodeContent = contentOption ? contentOption.label : '-'

      // 外部系統廠商
      const externalOption = qrcodeSourceOptions.value.find(
        opt => opt.value === configData.externalType,
      )
      const externalType = externalOption ? externalOption.label : '-'

      return {
        enable,
        cardSource,
        qrcodeContent,
        externalType,
      }
    })

    // 取得會員卡 QRCode 設定
    const getMemberCenterQRCode = async () => {
      const [res, err] = await GetMemberCenterQRCode({ shopId: shopId.value })
      if (err) {
        window.$message.error(err)
        return
      }
      for (const key in res) {
        set(configData, key, res[key])
      }
    }

    // 取得外部系統廠商選項
    const getQrcodeSourceOptions = async () => {
      const [res, err] = await GetMemberCenterQRCodeSourceOptions({ shopId: shopId.value })
      if (err) {
        window.$message.error(err)
        return
      }
      qrcodeSourceOptions.value = res
    }

    // 刷新資料
    const refresh = async () => {
      loading.value = true
      await Promise.all([
        getMemberCenterQRCode(),
        getQrcodeSourceOptions(),
      ])
      loading.value = false
      emit('refresh')
    }

    onMounted(() => {
      refresh()
    })

    return {
      loading,
      showEditModal,
      configData,
      qrcodeSourceOptions,
      displayData,
      refresh,
    }
  },
})
</script>

<style lang="postcss" scoped>
</style>
